/**
 * Browser-side alert for a finished Research Assistant run.
 *
 * Pure DOM helpers — no React, no Redux — so the decision rule and the two
 * alert surfaces (tab title badge, native notification) can be unit-tested
 * directly. ``useRunCompletionNotifier`` wires them to the run lifecycle.
 */

/** Runs shorter than this finish while the user is still looking at the reply. */
export const MIN_RUN_DURATION_MS = 10_000;

export const RUN_COMPLETE_TITLE_PREFIX = '✓ ';

export interface RunCompletionContext {
  /** ``null`` when the run's start was not observed live (e.g. reload mid-run). */
  durationMs: number | null;
  /** The tab is hidden or the window has lost focus. */
  tabInactive: boolean;
  /** The finished run belongs to the session currently on screen. */
  isActiveSession: boolean;
  cancelled: boolean;
}

/**
 * Alert when the user cannot already see the result: the tab is inactive, or
 * the run belongs to a session other than the one on screen. Cancelled runs
 * never alert (the user stopped them), and short runs are skipped so quick
 * replies don't ping.
 */
export const shouldAlertRunCompletion = ({
  durationMs,
  tabInactive,
  isActiveSession,
  cancelled,
}: RunCompletionContext): boolean => {
  if (cancelled) return false;
  if (!tabInactive && isActiveSession) return false;
  if (durationMs !== null && durationMs < MIN_RUN_DURATION_MS) return false;
  return true;
};

export const isTabInactive = (): boolean =>
  document.visibilityState === 'hidden' || !document.hasFocus();

let originalTitle: string | null = null;
let restoreListenersAttached = false;

const restoreTitle = (): void => {
  if (isTabInactive() || originalTitle === null) return;
  document.title = originalTitle;
  originalTitle = null;
};

/** Prefixes the tab title until the user returns to the tab. */
export const badgeTabTitle = (): void => {
  if (originalTitle === null) {
    originalTitle = document.title;
    document.title = `${RUN_COMPLETE_TITLE_PREFIX}${originalTitle}`;
  }
  if (!restoreListenersAttached) {
    document.addEventListener('visibilitychange', restoreTitle);
    window.addEventListener('focus', restoreTitle);
    restoreListenersAttached = true;
  }
};

/**
 * Asks for notification permission. Browsers only honour the prompt when it
 * follows a user gesture, so call it from a send handler, not a socket event.
 */
export const requestNotificationPermission = (): void => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  void Notification.requestPermission();
};

const openNotifications = new Map<string, Notification>();

/**
 * Shows a native notification only when permission was already granted.
 * A new notification that shares a tag with one still on screen replaces it
 * silently in most browsers, so the previous one is closed first.
 */
export const showNativeNotification = (title: string, body: string, tag: string): void => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  openNotifications.get(tag)?.close();
  const notification = new Notification(title, { body, tag });
  openNotifications.set(tag, notification);
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
  notification.onclose = () => {
    if (openNotifications.get(tag) === notification) openNotifications.delete(tag);
  };
};

let audioContext: AudioContext | null = null;

/**
 * Creates the audio context during a user gesture. Browsers keep an audio
 * context suspended until the page has had user activation, so the chime can
 * only play later from a socket event if the context was started by a send.
 */
export const primeChime = (): void => {
  if (typeof AudioContext === 'undefined') return;
  audioContext ??= new AudioContext();
  if (audioContext.state === 'suspended') void audioContext.resume();
};

const CHIME_NOTES_HZ = [880, 1318.5];
const CHIME_NOTE_SECONDS = 0.18;

/** Plays a short two-note chime. Silent when audio was never primed or is blocked. */
export const playChime = (): void => {
  if (audioContext === null || audioContext.state !== 'running') return;
  const context = audioContext;
  CHIME_NOTES_HZ.forEach((frequency, index) => {
    const start = context.currentTime + index * CHIME_NOTE_SECONDS;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + CHIME_NOTE_SECONDS);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + CHIME_NOTE_SECONDS);
  });
};
